import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import QRCode from "react-qr-code";
import { ReactComponent as Battery } from "./icons/battery.svg";
import { ReactComponent as CurvedArrow } from "./icons/curved-arrow-qr.svg";
import { ReactComponent as IPhoneSignal } from "./icons/iphone_signal.svg";
import { ReactComponent as Wifi } from "./icons/wifi.svg";

type PreviewPlatform = "IOS" | "ANDROID";

const getPreviewAppUrl = (activeBrandID: string) => {
  const decodedBrandId = window.atob(activeBrandID).split(":")[1];
  return "kinstudio://load-brand?id=" + decodedBrandId;
};

const MobileDisplay = ({
  children,
  platform = "IOS",
}: {
  children: React.ReactNode;
  platform?: PreviewPlatform;
}) => {
  const theme = useTheme();
  const isIOS = platform === "IOS";
  const deviceWidth = isIOS ? 390 : 375;
  const cutout =
    platform === "ANDROID" ? (
      <Box
        width="12px"
        height="12px"
        bgcolor="#000000"
        borderRadius="50%"
        position="absolute"
        left="50%"
        top="50%"
        sx={{ transform: "translate(-50%, -50%)" }}
      />
    ) : (
      <Box
        width="90px"
        height="28px"
        bgcolor="#000000"
        borderRadius="20px"
        position="absolute"
        left="50%"
        top="calc(50% + 8px)"
        sx={{ transform: "translate(-50%, -50%)" }}
      />
    );

  return (
    <Box
      display="flex"
      justifyContent="center"
      bgcolor="#f1f3f6"
      paddingX="16px"
      paddingY="60px"
    >
      <Box
        display="flex"
        bgcolor="white"
        borderRadius={isIOS ? "44px" : "20px"}
        boxShadow="0px 8px 24px 0px #00000026"
        position="relative"
        overflow="hidden"
        height={`calc(${deviceWidth}px * (19.5 / 9))`}
        width="100%"
        maxWidth={`${deviceWidth}px`}
      >
        <Box
          width="100%"
          height="33px"
          position="absolute"
          top="0"
          left="0"
          zIndex={1000}
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          padding={isIOS ? "0px 28px" : "0px 15px"}
        >
          <Typography variant="body0">
            {new Date().toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              hour12: false,
            })}
          </Typography>
          {cutout}
          <Stack direction="row" alignItems={"center"} spacing={1}>
            <Wifi
              height={13}
              width={18}
              color={theme.palette.navigationForeground.main}
            />
            <IPhoneSignal
              height={13}
              width={17}
              color={theme.palette.navigationForeground.main}
            />
            <Battery
              height={13}
              width={27}
              color={theme.palette.navigationForeground.main}
            />
          </Stack>
        </Box>
        <Box width="100%" height="calc(100% - 33px)" marginTop="33px">
          {children}
        </Box>
      </Box>
    </Box>
  );
};

const PreviewAppQrContent = ({ activeBrandID }: { activeBrandID: string }) => {
  const previewAppUrl = getPreviewAppUrl(activeBrandID);

  return (
    <Box display="flex" flexDirection="column">
      <Box position="relative">
        <Typography variant="h6" fontSize="16px" sx={{ fontSize: "16px" }}>
          Scan QR code to test
        </Typography>
        <CurvedArrow
          style={{
            position: "absolute",
            top: "10px",
            right: "0",
          }}
        />
      </Box>
      <Typography
        sx={{
          marginTop: "16px",
          fontSize: "16px",
          color: "text.secondary",
          fontWeight: 400,
        }}
      >
        To test on your device:
        <br />
        1. Open Camera app
        <br />
        2. Scan the QR code above
      </Typography>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="250px"
        width="250px"
        sx={{
          marginTop: "32px",
          background: "#00000005",
          border: "1px solid var(--border_light, #F0F0F0)",
          borderRadius: "16px",
        }}
      >
        <QRCode
          size={256}
          style={{ height: "auto", maxWidth: "100%", width: "80%" }}
          value={previewAppUrl}
          viewBox={`0 0 256 256`}
        />
      </Box>
    </Box>
  );
};

export const PreviewAppQrDialog = ({
  activeBrandID,
  open,
  onClose,
}: {
  activeBrandID: string;
  open: boolean;
  onClose: () => void;
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="preview-app-dialog-title"
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "360px",
          },
        },
      }}
    >
      <DialogTitle id="preview-app-dialog-title">
        Preview in Kin Studio
      </DialogTitle>
      <DialogContent>
        <PreviewAppQrContent activeBrandID={activeBrandID} />
      </DialogContent>
    </Dialog>
  );
};

const MobilePreviewWithQR = ({
  children,
  showMobileDisplay,
  platform = "IOS",
}: {
  children: React.ReactNode;
  showMobileDisplay: boolean;
  platform?: PreviewPlatform;
}) => {
  return (
    <Box width="100%" position="relative">
      {showMobileDisplay ? (
        <MobileDisplay platform={platform}>{children}</MobileDisplay>
      ) : (
        <Box width="100%" padding="80px">
          {children}
        </Box>
      )}
    </Box>
  );
};

export default MobilePreviewWithQR;
